import { useState, useEffect, useCallback } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { 
  X, FileText, CheckCircle, XCircle, AlertTriangle, 
  Clock, Ban, Mail, TrendingUp, Download, Copy
} from "lucide-react";

export default function CampaignReportModal({ campaignId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/campaigns/${campaignId}/report`);
      setReport(response.data);
    } catch (error) {
      toast.error("Failed to load campaign report");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent":
      case "delivered":
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case "bounced":
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case "blocked":
        return <Ban className="h-4 w-4 text-red-500" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <Clock className="h-4 w-4 text-blue-500" />;
      default:
        return <Mail className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      delivered: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      bounced: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      blocked: "bg-red-500/10 text-red-500 border-red-500/20",
      failed: "bg-red-500/10 text-red-500 border-red-500/20",
      pending: "bg-blue-500/10 text-blue-500 border-blue-500/20"
    };
    return (
      <Badge className={`${styles[status] || "bg-muted"} border`}>
        {status}
      </Badge>
    );
  };

  const copyReportToClipboard = () => {
    if (!report) return;
    
    const textContent = [
      `Campaign Report: ${report.campaign_name}`,
      `Status: ${report.status}`,
      `Sent At: ${report.sent_at ? new Date(report.sent_at).toLocaleString() : 'N/A'}`,
      ``,
      `Summary:`,
      `- Total Recipients: ${report.summary?.total_recipients || 0}`,
      `- Delivered: ${report.summary?.delivered || 0}`,
      `- Failed: ${report.summary?.failed || 0}`,
      `- Bounced: ${report.summary?.bounced || 0}`,
      `- Blocked: ${report.summary?.blocked || 0}`,
      `- Delivery Rate: ${report.summary?.delivery_rate || 0}%`,
      ``,
      `Recipients:`,
      ...report.recipients.map(r => `- ${r.email}: ${r.status}${r.error ? ` (Error: ${r.error})` : ''}`)
    ].join("\n");
    
    navigator.clipboard.writeText(textContent).then(() => {
      toast.success("Report copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy to clipboard");
    });
  };

  const exportReport = async (statusFilter = "") => {
    if (!report) return;
    
    try {
      // Use backend endpoint to download CSV
      const query = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
      const response = await api.get(`/campaigns/${campaignId}/report/csv${query}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix = statusFilter ? `-${statusFilter.replace(/,/g, "_")}` : "";
      link.download = `campaign-report-${report.campaign_name || campaignId}${suffix}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Report downloaded!");
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback: Copy to clipboard
      copyReportToClipboard();
      toast.info("Download failed. Report copied to clipboard instead.");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay p-4">
        <div className="bg-card border border-border rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-primary" />
            <div>
              <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Campaign Report
              </h2>
              <p className="text-sm text-muted-foreground">{report?.campaign_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyReportToClipboard}>
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportReport("")}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-10rem)] p-6 space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-secondary/30 border-border">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-foreground">
                  {report?.summary?.total_recipients || 0}
                </div>
                <div className="text-xs text-muted-foreground">Total Recipients</div>
              </CardContent>
            </Card>
            
            <Card className="bg-emerald-500/10 border-emerald-500/20">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                  <span className="text-2xl font-bold text-emerald-500">
                    {report?.summary?.delivered || 0}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">Delivered</div>
              </CardContent>
            </Card>
            
            <Card className="bg-red-500/10 border-red-500/20">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <XCircle className="h-5 w-5 text-red-500" />
                  <span className="text-2xl font-bold text-red-500">
                    {report?.summary?.failed || 0}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">Failed</div>
              </CardContent>
            </Card>
            
            <Card className="bg-amber-500/10 border-amber-500/20">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <span className="text-2xl font-bold text-amber-500">
                    {report?.summary?.bounced || 0}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">Bounced</div>
              </CardContent>
            </Card>
            
            <Card className="bg-red-500/10 border-red-500/20">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Ban className="h-5 w-5 text-red-500" />
                  <span className="text-2xl font-bold text-red-500">
                    {report?.summary?.blocked || 0}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">Blocked</div>
              </CardContent>
            </Card>
          </div>

          {/* Delivery Rates */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Delivery Rates</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Delivery Rate</span>
                    <span className="text-sm font-medium text-emerald-500">
                      {report?.summary?.delivery_rate || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${report?.summary?.delivery_rate || 0}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Bounce Rate</span>
                    <span className="text-sm font-medium text-amber-500">
                      {report?.summary?.bounce_rate || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-500 rounded-full transition-all"
                      style={{ width: `${report?.summary?.bounce_rate || 0}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Failure Rate</span>
                    <span className="text-sm font-medium text-red-500">
                      {report?.summary?.failure_rate || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-red-500 rounded-full transition-all"
                      style={{ width: `${report?.summary?.failure_rate || 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recipient Details */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">Recipient Details</h3>
              {report?.has_more_recipients && (
                <span className="text-xs text-muted-foreground">
                  Showing first 100 recipients
                </span>
              )}
            </div>
            
            {report?.recipients?.length > 0 ? (
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Sent At
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Error
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {report.recipients.map((recipient, index) => (
                      <tr key={index} className="hover:bg-secondary/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(recipient.status)}
                            <span className="text-sm text-foreground">{recipient.email}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(recipient.status)}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {recipient.sent_at ? new Date(recipient.sent_at).toLocaleString() : "-"}
                        </td>
                        <td className="px-4 py-3">
                          {recipient.error ? (
                            <span className="text-xs text-red-400 truncate block max-w-[200px]" title={recipient.error}>
                              {recipient.error}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground border border-border rounded-lg">
                <Mail className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>No recipient details available</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {report?.sent_at && (
              <span>Sent on {new Date(report.sent_at).toLocaleString()}</span>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
